import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { campaignTemplatesMap } from '../../templates';
import { getUserTimezone } from '@ui/calendar/utils';

interface TemplateThumbnailProps {
  templateUuid: string;
}

// Renders a full email template scaled down to fit its container. Templates are large component trees, so the
// thumbnail only mounts once it is near the viewport, and scaling is applied straight to the DOM so resizing
// never re-renders the template.
export default function TemplateThumbnail({ templateUuid }: TemplateThumbnailProps) {
  const event = useAppSelector(getWorkingProject);
  const containerRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');
  const timezone = useMemo(() => getUserTimezone(), []);
  const TemplateComponent = campaignTemplatesMap.get(templateUuid);

  useEffect(() => {
    const container = containerRef.current;
    if (inView || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [inView]);

  useLayoutEffect(() => {
    const node = templateRef.current;
    const container = containerRef.current;
    if (!node || !container) return;

    node.inert = true;

    const updateScale = () => {
      const { offsetWidth: width } = node;
      if (!width) return;
      const scale = container.clientWidth / width;
      node.style.transform = `scale(${scale})`;
      node.style.left = `${(container.clientWidth - width * scale) / 2}px`;
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    observer.observe(container);
    return () => observer.disconnect();
  }, [inView, event, TemplateComponent]);

  // memoized so parent re-renders (hover state, etc.) don't re-render the whole email tree
  const template = useMemo(
    () =>
      event && TemplateComponent ? (
        <TemplateComponent
          event={event}
          timezone={timezone}
          // uncomment to render with variables for easier exporting to other repos
          // calendarLinks={{
          //   iCal: '${icalLink}',
          //   outlook: '${outlookLink}',
          //   google: '${googleLink}',
          // }}
          // customizations={{
          //   subject: '',
          //   title: '${title}',
          //   description: '${description}',
          //   cta: '${cta}',
          //   startIntro: '${startIntro}',
          //   salutation: '${salutation}',
          //   headingFont: 'var(--heading-font)',
          //   bodyFont: 'var(--body-font)',
          //   headingImage: '${headingImage}',
          //   mainImage: '${mainImage}',
          //   headingTextColor: 'var(--heading-text-color)',
          //   bodyTextColor: 'var(--body-text-color)',
          //   backgroundColor: 'var(--background-color)',
          //   accentColor: 'var(--accent-color)',
          //   secondaryAccentColor: 'var(--secondary-accent-color)',
          //   secondaryBackgroundColor: 'var(--secondary-background-color)',
          //   secondaryImage: '${secondaryImage}',
          //   containerColor: 'var(--container-color)',
          //   buttonTextColor: 'var(--button-text-color)',
          // }}
        />
      ) : null,
    [event, TemplateComponent, timezone]
  );

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', overflow: 'hidden', height: '100%', width: '100%', borderRadius: '12px' }}
    >
      {inView && template && (
        <div
          ref={templateRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 'max-content',
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        >
          {template}
        </div>
      )}
    </div>
  );
}
